// hooks/useHandleError.ts
import axios, { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { errorReportingService } from "../services/auth/errorReportingService";
import { AuthErrorCategory } from "../types/auth/AuthError";
import { UKErrorMessages } from "../components/auth/ErrorMessage";

export const useHandleError = () => {
  const { enqueueSnackbar } = useSnackbar();

  const handleError = (error: unknown, operation: string = 'unknown') => {
    if (axios.isAxiosError(error)) {
      // Handle Axios error with proper categorisation
      const serverError = error as AxiosError<ApiErrorResponse>;

      const authError = errorReportingService.reportNetworkError({
        message: serverError.response?.data?.error || 'Network request failed',
        operation,
        code: serverError.code,
        statusCode: serverError.response?.status,
        endpoint: serverError.config?.url,
        retryable: !serverError.response || serverError.response.status >= 500,
        context: {
          method: serverError.config?.method,
          url: serverError.config?.url,
          responseData: serverError.response?.data
        }
      });

      // Use UK English messaging
      let displayMessage: string;
      if (serverError.response?.status === 401) {
        displayMessage = UKErrorMessages.INVALID_CREDENTIALS;
      } else if (serverError.response?.status === 429) {
        displayMessage = 'You\'re making requests too quickly. Please wait a moment and try again.';
      } else if (serverError.response?.status === 503) {
        displayMessage = UKErrorMessages.SERVER_MAINTENANCE;
      } else if (!navigator.onLine) {
        displayMessage = UKErrorMessages.NETWORK_OFFLINE;
      } else {
        displayMessage = serverError.response?.data?.error || UKErrorMessages.NETWORK_TIMEOUT;
      }

      enqueueSnackbar(displayMessage, {
        variant: "error",
        preventDuplicate: true,
      });

    } else if (error instanceof Error) {
      // Handle generic error messages with structured reporting
      const authError = errorReportingService.reportSystemError({
        message: error.message || 'Generic error occurred',
        operation,
        code: 'GENERIC_ERROR',
        error: error,
        context: { errorName: error.name, stack: error.stack }
      });

      // Map common error messages to UK English
      let displayMessage = error.message;

      if (error.message.includes('password') && error.message.includes('requirements')) {
        displayMessage = UKErrorMessages.WEAK_PASSWORD;
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        displayMessage = UKErrorMessages.NETWORK_TIMEOUT;
      } else if (error.message.includes('session') && error.message.includes('expired')) {
        displayMessage = UKErrorMessages.SESSION_EXPIRED;
      } else if (error.message.includes('browser') || error.message.includes('unsupported')) {
        displayMessage = UKErrorMessages.UNSUPPORTED_BROWSER;
      }

      enqueueSnackbar(displayMessage, {
        variant: "error",
        preventDuplicate: true,
      });

    } else {
      // Handle unknown errors
      const authError = errorReportingService.reportSystemError({
        message: 'Unknown error occurred',
        operation,
        code: 'UNKNOWN_ERROR',
        context: { error: String(error) }
      });

      enqueueSnackbar("Something unexpected happened. Please try again.", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  };

  return handleError;
};

interface ApiErrorResponse {
  error: string;
  status: number;
}
